import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUI } from '../context/UIContext';
import { useTutorial, TUTORIAL_STEPS } from '../context/TutorialContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useGuestPrompt } from '../context/GuestPromptContext';
import { isGuest } from '../lib/guestMode';
import AddParentModal from './AddParentModal';

// Icons for action sheet
const CardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M9 21V9" />
  </svg>
);

const BinderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <path d="M8 7h8" />
    <path d="M8 11h6" />
  </svg>
);

const StackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="6" rx="1" />
    <rect x="4" y="14" width="16" height="6" rx="1" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

// Search action sheet icons
const InboxIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

const ChatBubbleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Bottom Nav Icons
const CollectionIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const MessageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const HomeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hideNav, showAddOptions, setShowAddOptions, setAddAction, setShowPostModal, showPostModal, chatBadgeCount, closeAllModals } = useUI();
  const { isNavigationBlocked, currentStep, handleCardsNavClicked, handleAddButtonClicked, handlePostNavClicked, handleAddCardBinderComplete, handleTradeChatNavClicked } = useTutorial();
  const { isViewerOnly, userProfile, activeProfile } = useProfile();
  const { user } = useAuth();
  const { promptSignIn } = useGuestPrompt();
  const guestMode = !user && isGuest();
  const pricingEnabled = activeProfile?.pricing_trigger && activeProfile?.pricing_enabled === true;
  const { theme, isDark } = useTheme();
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [showParentModal, setShowParentModal] = useState(false);
  const [parentModalFeature, setParentModalFeature] = useState('this feature');

  const isOnCardsPage = location.pathname === '/cards';
  const isOnChatPage = location.pathname === '/chat' ||
    location.pathname.startsWith('/chat/') ||
    location.pathname === '/requests' ||
    location.pathname === '/messages' ||
    location.pathname === '/trades' ||
    location.pathname.startsWith('/trade/');
  const isOnShopsPage = location.pathname === '/locations';

  // Determine center pill label and icon
  const getCenterPillConfig = () => {
    if (isOnChatPage) return { label: 'Search', icon: SearchIcon };
    if (isOnShopsPage) return { label: 'Search', icon: SearchIcon };
    if (isOnCardsPage) return { label: 'Add', icon: PlusIcon };
    return { label: 'Post', icon: PlusIcon };
  };
  const centerPillConfig = getCenterPillConfig();

  const tabs = [
    { id: 'home', icon: HomeIcon, label: 'Home', path: '/home' },
    { id: 'cards', icon: CollectionIcon, label: 'Cards', path: '/cards' },
    { id: 'post', icon: centerPillConfig.icon, label: centerPillConfig.label, isPill: true, path: '/post' },
    { id: 'locations', icon: MapPinIcon, label: 'Shops', path: '/locations' },
    { id: 'chat', icon: MessageIcon, label: 'Chat', path: '/chat' },
  ];

  const getActiveTab = () => {
    const path = location.pathname;
    // Check if viewing a user's collection (Full Binder link)
    if (path.includes('/collection')) {
      return 'cards';
    }
    // Check if on any chat/meetup related page
    if (path === '/chat' || path.startsWith('/chat/') ||
        path === '/requests' || path === '/messages' ||
        path === '/trades' || path.startsWith('/trade/')) {
      return 'chat';
    }
    // Profile and other non-nav pages - don't highlight anything
    if (path === '/profile' || path === '/profile-picker' || path.startsWith('/settings')) {
      return null;
    }
    const tab = tabs.find(t => t.path === path);
    return tab ? tab.id : 'home';
  };

  const activeTab = getActiveTab();

  const handleTabPress = (tab) => {
    // Close any open modals/sheets when navigating
    closeAllModals();
    setShowSearchOptions(false);

    // Guest mode: all protected tabs navigate; pages render their own empty state.

    // Viewer-only restriction: Block Chat tab (only for kid profiles, not guests)
    if (tab.id === 'chat' && isViewerOnly && !guestMode) {
      setParentModalFeature('chat and messaging');
      setShowParentModal(true);
      return;
    }

    // Special case: Allow Cards nav during ADD_CARD_BINDER_NAV step
    if (tab.id === 'cards' && currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_NAV) {
      handleCardsNavClicked();
      navigate(tab.path);
      return;
    }
    // If user clicks other tabs during ADD_CARD_BINDER_NAV, end the tutorial
    if (currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_NAV && tab.id !== 'cards') {
      handleAddCardBinderComplete();
      navigate(tab.path);
      return;
    }
    // Special case: Allow Chat nav during TRADE_CHAT_NAV step
    if (tab.id === 'chat' && currentStep === TUTORIAL_STEPS.TRADE_CHAT_NAV) {
      handleTradeChatNavClicked();
      navigate(tab.path);
      return;
    }
    if (isNavigationBlocked) return; // Block during tutorial
    navigate(tab.path);
  };

  const handleAddPress = () => {
    // If any modal/sheet is already open, close everything first (toggle behavior)
    if (showAddOptions || showSearchOptions || showPostModal) {
      closeAllModals();
      setShowSearchOptions(false);
      return;
    }

    // Guest mode: posting requires an account
    if (guestMode) {
      promptSignIn('create a post');
      return;
    }

    // Viewer-only restriction: Block Post button (kid profiles only, not guests)
    if (isViewerOnly && !guestMode && !isOnCardsPage && !isOnChatPage) {
      setParentModalFeature('posting to the community');
      setShowParentModal(true);
      return;
    }

    // Allow Add press during ADD_CARD_BINDER_BUTTONS step, set flow flag for congrats later
    if (currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_BUTTONS) {
      handleAddButtonClicked();
      // Continue to open the action sheet
    } else if (currentStep === TUTORIAL_STEPS.POST_CARD_NAV) {
      // Post Card tutorial - clear tutorial and open post modal
      handlePostNavClicked();
      setShowPostModal(true);
      return;
    } else if (isNavigationBlocked) {
      return; // Block during other tutorial steps
    }
    if (isOnChatPage) {
      setShowSearchOptions(true);
    } else if (isOnShopsPage) {
      navigate('/locations?search=true');
    } else if (isOnCardsPage) {
      setShowAddOptions(true);
    } else {
      setShowPostModal(true);
    }
  };

  const handleSearchOption = (option) => {
    setShowSearchOptions(false);
    switch (option) {
      case 'requests':
        navigate('/requests?search=true');
        break;
      case 'messages':
        navigate('/messages?search=true');
        break;
      case 'scheduled':
        navigate('/trades?search=true');
        break;
      default:
        break;
    }
  };

  const handleAddOption = (action) => {
    setAddAction(action);
    setShowAddOptions(false);
  };

  const leftTabs = tabs.slice(0, 2);
  const rightTabs = tabs.slice(3);
  const postTab = tabs[2];

  if (hideNav) return null;

  // Dynamic styles using theme
  const navStyles = {
    bottomNav: {
      ...styles.bottomNav,
      backgroundColor: theme.bgPure,
      boxShadow: isDark ? 'none' : theme.shadowNav,
      border: isDark ? 'none' : `1px solid ${theme.border}`,
    },
    navBtn: {
      ...styles.navBtn,
      color: theme.textSecondary,
    },
    navBtnActive: {
      ...styles.navBtnActive,
      backgroundColor: theme.bgCard,
      borderColor: theme.accent,
      color: theme.accent,
    },
    navBtnPill: {
      ...styles.navBtnPill,
      backgroundColor: theme.bg,
      borderColor: theme.accent,
      color: theme.accent,
    },
    badge: {
      ...styles.badge,
      borderColor: theme.bgPure,
    },
    actionSheet: {
      ...styles.actionSheet,
      backgroundColor: theme.bgSheet,
      borderColor: theme.border,
    },
    actionSheetOption: {
      ...styles.actionSheetOption,
      backgroundColor: theme.bgCard,
      borderColor: theme.border,
    },
  };

  return (
    <>
    <nav style={navStyles.bottomNav}>
      {/* Left group */}
      <div style={styles.navGroup}>
        {leftTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              data-tutorial={`nav-${tab.id}`}
              style={{
                ...navStyles.navBtn,
                ...(isActive ? navStyles.navBtnActive : {}),
              }}
              onClick={() => handleTabPress(tab)}
            >
              <Icon />
              <span style={{
                ...styles.navLabel,
                color: isActive ? theme.accent : 'inherit',
              }}>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Center Post/Add/Search pill */}
      <button
        data-tutorial="nav-add"
        style={{
          ...navStyles.navBtnPill,
          ...(activeTab === postTab.id ? { backgroundColor: theme.bgHover } : {}),
        }}
        onClick={handleAddPress}
      >
        {(isOnChatPage || isOnShopsPage) ? <SearchIcon /> : <PlusIcon />}
        <span style={styles.navPillLabel}>{postTab.label}</span>
      </button>

      {/* Right group */}
      <div style={styles.navGroup}>
        {rightTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const showBadge = tab.id === 'chat' && chatBadgeCount > 0;
          return (
            <button
              key={tab.id}
              data-tutorial={`nav-${tab.id}`}
              style={{
                ...navStyles.navBtn,
                ...(isActive ? navStyles.navBtnActive : {}),
              }}
              onClick={() => handleTabPress(tab)}
            >
              <div style={styles.iconWrapper}>
                <Icon />
                {showBadge && (
                  <div style={navStyles.badge}>
                    <span style={styles.badgeText}>
                      {chatBadgeCount > 99 ? '99+' : chatBadgeCount}
                    </span>
                  </div>
                )}
              </div>
              <span style={{
                ...styles.navLabel,
                color: isActive ? theme.accent : 'inherit',
              }}>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>

      {/* Add Options Action Sheet */}
      {showAddOptions && (
        <>
          <div
            style={{...styles.actionSheetBackdrop, backgroundColor: theme.overlayLight}}
            onClick={() => setShowAddOptions(false)}
          />
          <div style={navStyles.actionSheet}>
            <div style={{...styles.actionSheetHeader, borderColor: theme.border}}>
              <span style={{...styles.actionSheetTitle, color: theme.text}}>Add to Collection</span>
              <button
                style={{...styles.actionSheetClose, backgroundColor: theme.bgCard, color: theme.textSecondary}}
                onClick={() => setShowAddOptions(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div style={styles.actionSheetOptions}>
              <button
                style={navStyles.actionSheetOption}
                onClick={() => handleAddOption('card')}
              >
                <div style={{...styles.actionSheetOptionIcon, backgroundColor: theme.accentBg, color: theme.accent}}>
                  <CardIcon />
                </div>
                <div style={styles.actionSheetOptionText}>
                  <span style={{...styles.actionSheetOptionTitle, color: theme.text}}>Add Card</span>
                  <span style={{...styles.actionSheetOptionDesc, color: theme.textSecondary}}>Add up to 20 cards at once</span>
                </div>
              </button>
              <button
                style={navStyles.actionSheetOption}
                onClick={() => handleAddOption('binder')}
              >
                <div style={{...styles.actionSheetOptionIcon, backgroundColor: theme.accentBg, color: theme.accent}}>
                  <BinderIcon />
                </div>
                <div style={styles.actionSheetOptionText}>
                  <span style={{...styles.actionSheetOptionTitle, color: theme.text}}>Add Binder</span>
                  <span style={{...styles.actionSheetOptionDesc, color: theme.textSecondary}}>Create a new binder</span>
                </div>
              </button>
              {pricingEnabled && (
              <button
                style={navStyles.actionSheetOption}
                onClick={() => { setShowAddOptions(false); navigate('/fair-trade'); }}
              >
                <div style={{...styles.actionSheetOptionIcon, backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#22c55e'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                <div style={styles.actionSheetOptionText}>
                  <span style={{...styles.actionSheetOptionTitle, color: theme.text}}>Fair Trade</span>
                  <span style={{...styles.actionSheetOptionDesc, color: theme.textSecondary}}>Compare card values in a trade</span>
                </div>
              </button>
              )}
              {pricingEnabled && (
              <button
                style={navStyles.actionSheetOption}
                onClick={() => { setShowAddOptions(false); navigate('/bulk-scanner?mode=quickscan'); }}
              >
                <div style={{...styles.actionSheetOptionIcon, backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                </div>
                <div style={styles.actionSheetOptionText}>
                  <span style={{...styles.actionSheetOptionTitle, color: theme.text}}>Quick Scan</span>
                  <span style={{...styles.actionSheetOptionDesc, color: theme.textSecondary}}>Scan up to 100 cards for pricing</span>
                </div>
              </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Search Options Action Sheet */}
      {showSearchOptions && (
        <>
          <div
            style={{...styles.actionSheetBackdrop, backgroundColor: theme.overlayLight}}
            onClick={() => setShowSearchOptions(false)}
          />
          <div style={navStyles.actionSheet}>
            <div style={{...styles.actionSheetHeader, borderColor: theme.border}}>
              <span style={{...styles.actionSheetTitle, color: theme.text}}>Search</span>
              <button
                style={{...styles.actionSheetClose, backgroundColor: theme.bgCard, color: theme.textSecondary}}
                onClick={() => setShowSearchOptions(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div style={styles.actionSheetOptions}>
              <button
                style={navStyles.actionSheetOption}
                onClick={() => handleSearchOption('requests')}
              >
                <div style={{...styles.actionSheetOptionIcon, backgroundColor: theme.accentBg, color: theme.accent}}>
                  <InboxIcon />
                </div>
                <div style={styles.actionSheetOptionText}>
                  <span style={{...styles.actionSheetOptionTitle, color: theme.text}}>Search Requests</span>
                  <span style={{...styles.actionSheetOptionDesc, color: theme.textSecondary}}>Find trade requests by name or card</span>
                </div>
              </button>
              <button
                style={navStyles.actionSheetOption}
                onClick={() => handleSearchOption('messages')}
              >
                <div style={{...styles.actionSheetOptionIcon, backgroundColor: theme.accentBg, color: theme.accent}}>
                  <ChatBubbleIcon />
                </div>
                <div style={styles.actionSheetOptionText}>
                  <span style={{...styles.actionSheetOptionTitle, color: theme.text}}>Search Messages</span>
                  <span style={{...styles.actionSheetOptionDesc, color: theme.textSecondary}}>Find conversations by name</span>
                </div>
              </button>
              <button
                style={navStyles.actionSheetOption}
                onClick={() => handleSearchOption('scheduled')}
              >
                <div style={{...styles.actionSheetOptionIcon, backgroundColor: theme.accentBg, color: theme.accent}}>
                  <CalendarIcon />
                </div>
                <div style={styles.actionSheetOptionText}>
                  <span style={{...styles.actionSheetOptionTitle, color: theme.text}}>Search Scheduled</span>
                  <span style={{...styles.actionSheetOptionDesc, color: theme.textSecondary}}>Find scheduled meetups</span>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Add Parent Modal for viewer-only users */}
      <AddParentModal
        isOpen={showParentModal}
        onClose={() => setShowParentModal(false)}
        feature={parentModalFeature}
      />
    </>
  );
};

const styles = {
  bottomNav: {
    position: 'fixed',
    bottom: 'var(--safe-area-bottom, 0px)',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '8px 12px',
    backgroundColor: '#000000',
    borderRadius: '40px',
    zIndex: 10000,
  },
  navGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0px',
  },
  navBtn: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    cursor: 'pointer',
    color: '#6a6a6a',
    transition: 'all 0.2s ease',
  },
  navBtnActive: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #0ea5e9',
    color: '#0ea5e9',
  },
  iconWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -8,
    backgroundColor: '#ef4444',
    borderRadius: 10,
    minWidth: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
    border: '2px solid #000000',
  },
  badgeText: {
    fontSize: 9,
    fontWeight: 700,
    color: '#ffffff',
  },
  navLabel: {
    fontSize: '9px',
    fontWeight: '500',
    color: 'inherit',
  },
  navLabelActive: {
    color: '#0ea5e9',
  },
  navBtnPill: {
    minWidth: '130px',
    height: '48px',
    borderRadius: '24px',
    backgroundColor: '#0a0a0a',
    border: '2px solid #0ea5e9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '0 20px',
    cursor: 'pointer',
    color: '#0ea5e9',
    transition: 'all 0.2s ease',
  },
  navBtnPillActive: {
    backgroundColor: '#111111',
  },
  navPillLabel: {
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '0.3px',
  },
  // Action Sheet styles
  actionSheetBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 999,
  },
  actionSheet: {
    position: 'fixed',
    bottom: 'calc(100px + var(--safe-area-bottom, 0px))',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 32px)',
    maxWidth: '400px',
    backgroundColor: '#151515',
    borderRadius: '24px',
    border: '1px solid #2a2a2a',
    zIndex: 1000,
    overflow: 'hidden',
    minHeight: '200px',
  },
  actionSheetHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #2a2a2a',
  },
  actionSheetTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
  },
  actionSheetClose: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: '#1a1a1a',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#6a6a6a',
  },
  actionSheetOptions: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionSheetOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '18px 20px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '16px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s ease',
  },
  actionSheetOptionIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0ea5e9',
    flexShrink: 0,
  },
  actionSheetOptionText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  actionSheetOptionTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
  },
  actionSheetOptionDesc: {
    color: '#6a6a6a',
    fontSize: '13px',
    marginTop: '2px',
  },
};

export default BottomNav;
